import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FolderArrowDownIcon,
  StarIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import type { BulkAction } from '../../../../types/bookmark'
import { useTranslation } from 'react-i18next'

interface BulkActionBarProps {
  selectedCount: number
  isAllSelected: boolean
  isBusy?: boolean
  onSelectAll: () => void
  onClear: () => void
  onAction: (action: BulkAction, payload?: { isFavorite?: boolean }) => void
  onMoveRequest: () => void
}

/**
 * 다중 선택 시 나타나는 일괄 작업 바.
 * 선택 개수를 명시해 의도치 않은 대량 작업을 방지한다.
 */
export default function BulkActionBar({
  selectedCount,
  isAllSelected,
  isBusy = false,
  onSelectAll,
  onClear,
  onAction,
  onMoveRequest,
}: BulkActionBarProps) {
  const { t } = useTranslation()
  const [isFavoriteMenuOpen, setIsFavoriteMenuOpen] = useState(false)

  return (
    <AnimatePresence>
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          role="toolbar"
          aria-label={t('bookmark.bulk.toolbar')}
          className="fixed bottom-6 left-1/2 z-40 w-[min(calc(100%-2rem),44rem)] -translate-x-1/2 rounded-2xl bg-gray-900 p-2 shadow-[0_20px_40px_-16px_rgba(15,23,42,0.6)] dark:bg-gray-800 dark:ring-1 dark:ring-gray-700"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="ml-2 mr-2 text-sm font-bold tabular-nums text-white">
              {t('bookmark.bulk.selected', { count: String(selectedCount) })}
            </span>

            <button
              type="button"
              onClick={onSelectAll}
              className="rounded-[10px] px-2.5 py-1.5 text-sm text-gray-400 transition hover:bg-white/10 hover:text-white"
            >
              {isAllSelected
                ? t('bookmark.bulk.deselectAll')
                : t('bookmark.bulk.selectAll')}
            </button>

            <span className="mx-1 hidden h-5 w-px bg-gray-700 sm:block" />

            <button
              type="button"
              disabled={isBusy}
              onClick={onMoveRequest}
              className="inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
            >
              <FolderArrowDownIcon className="h-4 w-4" />
              {t('bookmark.bulk.move')}
            </button>

            <div className="relative">
              <button
                type="button"
                disabled={isBusy}
                aria-expanded={isFavoriteMenuOpen}
                onClick={() => setIsFavoriteMenuOpen(open => !open)}
                className="inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
              >
                <StarIcon className="h-4 w-4" />
                {t('bookmark.bulk.favorite')}
              </button>

              {isFavoriteMenuOpen && (
                <div className="absolute bottom-full left-0 mb-2 w-40 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {[
                    { label: t('bookmark.bulk.addFavorite'), value: true },
                    { label: t('bookmark.bulk.removeFavorite'), value: false },
                  ].map(option => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => {
                        setIsFavoriteMenuOpen(false)
                        onAction('favorite', { isFavorite: option.value })
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={isBusy}
              onClick={() => onAction('delete')}
              className="inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-sm font-semibold text-rose-300 transition hover:bg-rose-500/15 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              {t('bookmark.bulk.delete')}
            </button>

            <button
              type="button"
              onClick={onClear}
              aria-label={t('bookmark.bulk.clear')}
              className="ml-auto rounded-lg p-1.5 text-gray-500 transition hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
